import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';

import { Subject, Subscription } from 'rxjs';
import { first, map, takeUntil } from 'rxjs/operators';

import { filterBoolean } from '@recruitee/common';

import { IntersectionObserverArea } from '../../intersection-observer/intersection-observer-area';
import {
  DataTableCellContext,
  DataTableRow,
  DataTableSelectionCellContext,
} from '../data-table.types';

@Component({
  selector: 'rt-data-table-row',
  template: `
    <ng-container
      *ngTemplateOutlet="alwaysVisible || visible ? visibleTpl : hiddenTpl"
    ></ng-container>

    <ng-template #visibleTpl>
      <td
        *ngIf="selectable"
        (click)="!disabled && rowSelect.emit(!selected); $event.stopPropagation()"
        class="selection-cell"
      >
        <div class="cell-body">
          <ng-container *ngIf="selectionCellTemplate">
            <ng-container
              *ngTemplateOutlet="selectionCellTemplate; context: selectionCellContext"
            ></ng-container>
          </ng-container>
          <rt-checkbox
            *ngIf="!selectionCellTemplate"
            [disabled]="disabled"
            [checked]="selected"
            (valueChange)="rowSelect.emit(!!$event)"
          >
          </rt-checkbox>
        </div>
      </td>
      <td *ngFor="let columnId of columnIds; trackBy: trackByColumnId" class="data-cell">
        <div class="cell-body">
          <ng-container
            [ngTemplateOutlet]="cellTemplateMap[columnId] || defaultCellTemplate"
            [ngTemplateOutletContext]="cellsContexts[columnId]"
          >
          </ng-container>
        </div>
      </td>
    </ng-template>

    <ng-template #hiddenTpl>
      <td
        [attr.colspan]="columnIds.length + (selectable ? 1 : 0)"
        [style.verticalAlign]="'middle'"
        [style.padding]="'1rem'"
        [style.height]="notVisibleItemHeight"
      >
        <rt-placeloader
          [style.height]="'100%'"
          [style.width]="'30%'"
          class="disable-animations"
        ></rt-placeloader>
      </td>
    </ng-template>
  `,
  styleUrls: ['./data-table-row.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.selected]': 'selected',
    '[class.selectable]': 'selectable',
    '[class.clickable]': 'clickable',
    '[class.private]': 'private',
    '[class.hoverable]': 'hoverable',
    '[class.cellPadding]': 'cellPadding',
    '[class.is-zebra]': 'isZebra',
    '[class.disabled]': 'disabled',
  },
})
export class DataTableRowComponent implements OnChanges, OnDestroy {
  @Input() public index: number;
  @Input() public row: DataTableRow;
  @Input() public columnIds: string[];
  @Input() public cellTemplates: { [key: string]: TemplateRef<DataTableCellContext> } | undefined;
  @Input() public defaultCellTemplate: TemplateRef<DataTableCellContext>;
  @Input() public hoverable: boolean = false;
  @Input() public clickable: boolean = false;
  @Input() public selectable: boolean = false;
  @Input() public selectionCellTemplate:
    | TemplateRef<DataTableSelectionCellContext>
    | null
    | undefined;
  @Input() public selected: boolean = false;
  @Input() public private: boolean = false;
  @Input() public even: boolean = false;
  @Input() public cellPadding: boolean = true;
  @Input() public isZebra: boolean = true;
  @Input() public disabled: boolean = false;

  @Input() public alwaysVisible: boolean = true;
  @Input() public notVisibleItemHeight: string = '40px';

  @Output() public rowSelect: EventEmitter<boolean> = new EventEmitter();

  public visible: boolean = false;
  public cellTemplateMap: Record<string, TemplateRef<DataTableCellContext>> = {};

  public selectionCellContext: DataTableSelectionCellContext | null;
  public cellsContexts: { [key: string]: DataTableCellContext } = {};

  private ngOnDestroy$: Subject<void> = new Subject();
  private areaSub: Subscription | null;

  constructor(
    @Optional() private area: IntersectionObserverArea,
    private elRef: ElementRef,
    private cdRef: ChangeDetectorRef,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.alwaysVisible) {
      if (changes.alwaysVisible.currentValue === true) {
        this.visible = true;
      } else {
        if (this.areaSub) {
          this.areaSub.unsubscribe();
          this.areaSub = null;
        }

        if (this.area && this.area.serviceAvailable) {
          this.areaSub = this.area
            .intersectionChange(this.elRef)
            .pipe(
              map(entry => entry.isIntersecting),
              filterBoolean,
              first(),
              takeUntil(this.ngOnDestroy$),
            )
            .subscribe({
              next: () => this.show(),
              error: () => this.show(),
            });
        } else {
          this.visible = true;
        }
      }
    }

    if (changes.row || changes.columnIds || changes.cellTemplates) {
      this.cellTemplateMap = this.getCellTemplates();
      this.cellsContexts = this.columnIds.reduce<Record<string, DataTableCellContext>>(
        (acc, columnId, cellIndex) => {
          const cellContext: DataTableCellContext =
            !changes.row && this.cellsContexts[columnId]
              ? this.cellsContexts[columnId]
              : {
                  columnId,
                  row: this.row,
                  rowIndex: this.index,
                  rowEven: this.even,
                  cellIndex,
                };

          acc[columnId] = cellContext;
          return acc;
        },
        {},
      );
    }

    if (changes.selected && this.selectable && this.selectionCellTemplate) {
      this.selectionCellContext = {
        selected: this.selected,
        handleSelect: selected => this.rowSelect.emit(selected),
      };
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public trackByColumnId(index: number, columnId: string): string {
    return columnId;
  }

  private getCellTemplates() {
    return this.columnIds.reduce<Record<string, TemplateRef<DataTableCellContext>>>(
      (acc, columnId) => {
        const template =
          this.cellTemplates && this.cellTemplates[columnId]
            ? this.cellTemplates[columnId]
            : this.defaultCellTemplate;

        acc[columnId] = template;
        return acc;
      },
      {},
    );
  }

  private show(): void {
    if (!this.visible) {
      this.visible = true;
      this.cdRef.detectChanges();
    }
  }
}
